import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { getTestimonials } from '../../lib/api'
import Parallax from '../ui/Parallax'
import SectionTitle from '../ui/SectionTitle'
import LoadingSpinner from '../ui/LoadingSpinner'
import type { Testimonial } from '../../types'

export default function TestimonialCarousel() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([])
  const [current, setCurrent] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getTestimonials().then((res) => {
      setTestimonials(res)
      setLoading(false)
    })
  }, [])

  useEffect(() => {
    if (testimonials.length === 0) return
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % testimonials.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [testimonials.length])

  if (loading) return <LoadingSpinner text="Loading testimonials..." />

  const t = testimonials[current]

  return (
    <section className="relative py-24 overflow-hidden">
      {/* Background gradient */}
      <Parallax speed={0.2} className="absolute top-0 right-0 w-96 h-96">
        <div className="w-full h-full rounded-full bg-electric-purple/5 blur-[120px]" />
      </Parallax>
      <Parallax speed={0.15} className="absolute bottom-0 left-0 w-96 h-96">
        <div className="w-full h-full rounded-full bg-electric-blue/5 blur-[120px]" />
      </Parallax>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitle
          label="Testimonials"
          title="What Our Clients Say"
          subtitle="Don't take our word for it — hear from the businesses we've helped transform."
        />

        <div className="max-w-3xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              {/* Rating */}
              <div className="flex justify-center gap-1 mb-6">
                {Array.from({ length: 5 }).map((_, i) => (
                  <svg
                    key={i}
                    className={`w-5 h-5 ${
                      i < t.rating
                        ? 'text-yellow-400'
                        : 'text-gray-600'
                    }`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>

              {/* Quote */}
              <blockquote className="text-lg md:text-xl text-gray-300 leading-relaxed italic mb-8">
                &ldquo;{t.content}&rdquo;
              </blockquote>

              {/* Author */}
              <div className="flex items-center justify-center gap-4">
                <img
                  src={t.avatar}
                  alt={t.name}
                  className="w-12 h-12 rounded-full object-cover ring-2 ring-white/10"
                />
                <div className="text-left">
                  <div className="text-white font-semibold">{t.name}</div>
                  <div className="text-sm text-gray-500">
                    {t.role}, {t.company}
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-10">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  i === current
                    ? 'bg-electric-blue w-6'
                    : 'bg-white/10 hover:bg-white/20'
                }`}
                aria-label={`Go to testimonial ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
